import React, { ReactElement, FC, useMemo, useEffect, useRef } from 'react';
import { useIsLoggedIn } from 'modules/Auth/hooks';
import { useAuthActions } from 'modules/Auth/AuthContext';

type Config = {
  hideFor: 'NONE' | 'GUEST' | 'USER';
  placeholder?: ReactElement | null;
};

function makeConfig(): Config {
  return {
    hideFor: 'NONE',
    placeholder: null,
  };
}

export function withAuth<T>(config = makeConfig()): (c: FC<T>) => FC<T> {
  return (Component: FC<T>): FC<T> =>
    (props: T) => {
      const baseConfig = makeConfig();
      const { hideFor, placeholder } = {
        ...baseConfig,
        ...config,
      };

      const isLoggedIn = useIsLoggedIn();
      const { openLoginModal } = useAuthActions();
      const hasOpenedModal = useRef(false);

      const shouldRenderPlaceholder = getShouldRenderPlaceholder({ hideFor, isLoggedIn });

      useEffect(() => {
        if (shouldRenderPlaceholder && !hasOpenedModal.current) {
          openLoginModal();
          hasOpenedModal.current = true;
        }
      }, [shouldRenderPlaceholder, openLoginModal]);

      if (shouldRenderPlaceholder) {
        return placeholder || null;
      }

      return <Component {...{ ...props, isLoggedIn }} />;
    };
}

export function withAuthOptimized<T>(config = makeConfig()): (c: FC<T>) => FC<T> {
  return (Component: FC<T>): FC<T> =>
    (props: T) => {
      const baseConfig = makeConfig();
      const { hideFor, placeholder } = {
        ...baseConfig,
        ...config,
      };

      // Extract auth state
      const isLoggedIn = useIsLoggedIn();

      // Memoize the result of getShouldRenderPlaceholder
      const shouldRenderPlaceholder = useMemo(() => getShouldRenderPlaceholder({ hideFor, isLoggedIn }), [hideFor, isLoggedIn]);

      if (shouldRenderPlaceholder) {
        return placeholder || null;
      }

      // Memoize the props object to avoid creating a new reference
      const enhancedProps = useMemo(() => ({ ...props, isLoggedIn }), [props, isLoggedIn]);

      return <Component {...enhancedProps} />;
    };
}

function getShouldRenderPlaceholder({ hideFor, isLoggedIn }: { hideFor: 'NONE' | 'GUEST' | 'USER'; isLoggedIn: boolean }): boolean {
  if (hideFor === 'GUEST' && !isLoggedIn) {
    return true;
  }

  if (hideFor === 'USER' && isLoggedIn) {
    return true;
  }

  return false;
}

export const testable = { getShouldRenderPlaceholder };
